// src/pages/CompanyPublicWebsite.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import supabase from '../supabase';
import SEO from '../components/common/SEO';
import InstructorCoursesEmbed from '../components/university/marketplace/InstructorCoursesEmbed';
import { CompanyTeamPublicSection } from '../components/membership/PublicMembershipSections';

import { FiExternalLink, FiMapPin, FiPhone, FiMail, FiGlobe, FiArrowUp, FiPackage } from 'react-icons/fi';

const buildCompanyJsonLd = (data, slug) => {
  if (!data) return [];
  const url = `https://www.avlinknetwork.org/${slug}`;
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: data.companyName || data.name || data.title || 'Empresa',
      url,
      description: data.description || data.about || data.tagline,
      logo: data.logo || data.logoUrl || undefined,
      address: data.address
        ? {
            '@type': 'PostalAddress',
            streetAddress: data.address,
            addressLocality: data.city || undefined,
            addressCountry: data.country || undefined
          }
        : undefined,
      telephone: data.phone || undefined,
      email: data.email || undefined
    }
  ];
};

const CompanyPublicWebsite = () => {
  const { slug } = useParams();
  const [websiteData, setWebsiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 🔧 FUNCIÓN ARREGLADA para convertir rutas de Supabase en URLs completas
  const getSupabaseImageUrl = (imagePath, bucket = 'gallery-images') => {
    if (!imagePath) return null;
    
    console.log('🔧 Procesando imagen:', { imagePath, bucket });
    
    // Si ya es una URL completa válida, devolverla tal como está
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      // Verificar que no sea una blob URL malformada
      if (imagePath.includes('blob:')) {
        console.error('❌ Detectada URL blob malformada:', imagePath);
        return null;
      }
      return imagePath;
    }
    
    // Si es una ruta relativa, construir la URL completa de Supabase
    const supabaseUrl = supabase.supabaseUrl;
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    const fullUrl = `${supabaseUrl}/storage/v1/object/public/${bucket}/${cleanPath}`;
    
    console.log('✅ URL construida:', fullUrl);
    return fullUrl;
  };

  // 🔧 Función ARREGLADA para procesar imágenes de productos
  const processProductImages = (productsData) => {
    if (!productsData?.items || !Array.isArray(productsData.items)) {
      return productsData;
    }

    console.log('🔧 Procesando productos:', productsData.items.length);

    const processedItems = productsData.items.map((product, index) => {
      let processedImage = null;
      
      if (product.image) {
        // Para productos, usar el bucket correcto
        processedImage = getSupabaseImageUrl(product.image, 'company-products');
        
        console.log(`🔧 Producto ${index + 1} (${product.name}):`, {
          original: product.image,
          processed: processedImage
        });
      }

      return {
        ...product,
        image: processedImage
      };
    });

    return {
      ...productsData,
      items: processedItems
    };
  };

  // 🔧 Función para procesar las imágenes de la galería
  const processGalleryImages = (galleryData) => {
    if (!galleryData?.images || !Array.isArray(galleryData.images)) {
      return galleryData;
    }

    const processedImages = galleryData.images.map(image => {
      if (typeof image === 'string') {
        return {
          src: getSupabaseImageUrl(image, 'gallery-images'),
          alt: 'Imagen de galería'
        };
      } else if (image && typeof image === 'object') {
        return {
          ...image,
          src: getSupabaseImageUrl(image.src || image.path || image.url, 'gallery-images'),
          alt: image.alt || 'Imagen de galería'
        };
      }
      return image;
    }).filter(image => image.src);

    return {
      ...galleryData,
      images: processedImages
    };
  };

  useEffect(() => {
    const loadWebsite = async () => {
      try {
        setLoading(true);
        
        console.log('🔧 Cargando website con slug:', slug);
        
        // Buscar website por slug
        const { data: website, error: websiteError } = await supabase
          .from('company_websites')
          .select('*')
          .eq('slug', slug)
          .single();

        if (websiteError) {
          if (websiteError.code === 'PGRST116') {
            setError('Página no encontrada');
          } else {
            throw websiteError;
          }
          return;
        }

        if (!website || !website.is_published) {
          setError('Página no encontrada');
          return;
        }

        // Visibilidad: si la empresa eligió 'members' o 'private', no servimos
        // el contenido a visitantes anónimos. La página devuelve 'noindex' igual.
        if (website.profile_visibility && website.profile_visibility !== 'public') {
          setError('Este perfil no es público');
          return;
        }

        // 🔧 Procesar los datos del website
        const processedData = {
          ...website.data,
          slug: website.slug,
          companyId: website.company_id,
          isPublished: website.is_published,
          profileVisibility: website.profile_visibility,
          showInSearch: website.show_in_search
        };

        // Procesar galería
        if (processedData.gallery) {
          processedData.gallery = processGalleryImages(processedData.gallery);
        }

        // 🔧 Procesar productos con el bucket correcto
        if (processedData.products) {
          processedData.products = processProductImages(processedData.products);
        }

        // Procesar imagen del hero si existe
        if (processedData.hero?.backgroundImage) {
          processedData.hero.backgroundImage = getSupabaseImageUrl(processedData.hero.backgroundImage, 'hero-images');
        }

        // Procesar imagen de about si existe
        if (processedData.about?.image) {
          processedData.about.image = getSupabaseImageUrl(processedData.about.image, 'company-logos');
        }

        setWebsiteData(processedData);

        // Debug logging
        console.log('✅ Datos procesados del website:', processedData);
        if (processedData.products?.items) {
          console.log('✅ Productos procesados:', processedData.products.items);
        }

      } catch (err) {
        console.error('❌ Error loading website:', err);
        setError('Error al cargar la página');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadWebsite();
    }
  }, [slug]);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  // 🔧 Componente ARREGLADO para mostrar imágenes de productos con mejor manejo de errores
  const ProductImage = ({ product, index }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const handleImageError = (e) => {
      console.error(`❌ Error cargando imagen del producto "${product.name}":`, product.image);
      setImageError(true);
      setImageLoading(false);
    };

    const handleImageLoad = () => {
      console.log(`✅ Imagen del producto "${product.name}" cargada correctamente:`, product.image);
      setImageLoading(false);
    };

    // Si no hay imagen o hay error, mostrar placeholder
    if (!product.image || imageError) {
      return (
        <div className="w-full h-48 bg-gray-200 rounded-t-lg flex items-center justify-center">
          <div className="text-center text-gray-500">
            <FiPackage size={32} className="mx-auto mb-2" />
            <p className="text-sm">{imageError ? 'Error al cargar' : 'Sin imagen'}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative w-full h-48 bg-gray-100 rounded-t-lg overflow-hidden">
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-400">Cargando...</div>
          </div>
        )}
        <img 
          src={product.image} 
          alt={product.name || `Producto ${index + 1}`} 
          className="w-full h-full object-cover"
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: imageLoading ? 'none' : 'block' }}
        />
      </div>
    );
  };

  // 🔧 Componente para mostrar imágenes de galería
  const GalleryImage = ({ image, index }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    const handleImageError = (e) => {
      console.error('❌ Error cargando imagen de galería:', image.src);
      setImageError(true);
      setImageLoading(false);
    };

    const handleImageLoad = () => {
      console.log('✅ Imagen de galería cargada correctamente:', image.src);
      setImageLoading(false);
    };

    if (imageError || !image.src) {
      return (
        <div className="w-full h-48 bg-gray-200 rounded-lg flex items-center justify-center">
          <div className="text-center text-gray-500">
            <div className="text-2xl mb-2">🖼️</div>
            <p className="text-sm">Error al cargar imagen</p>
          </div>
        </div>
      );
    }

    return (
      <div className="relative rounded-lg overflow-hidden shadow-md">
        {imageLoading && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
            <div className="text-gray-400">Cargando...</div>
          </div>
        )}
        <img 
          src={image.src} 
          alt={image.alt || `Imagen ${index + 1}`} 
          className="w-full h-48 object-cover"
          onError={handleImageError}
          onLoad={handleImageLoad}
          style={{ display: imageLoading ? 'none' : 'block' }}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando página...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <SEO
          title={error}
          description="Página no disponible en Avlink Network."
          path={`/${slug}`}
          noindex
        />
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{error}</h1>
          <p className="text-gray-600 mb-6">
            La página que buscas no existe o no está disponible públicamente.
          </p>
          <a
            href="/"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    );
  }

  if (!websiteData) {
    return null;
  }

  const companyName = websiteData.companyName || websiteData.name || websiteData.title || 'Empresa';
  const companyDesc = websiteData.description || websiteData.about || websiteData.tagline
    || `${companyName} - Empresa del sector audiovisual asociada a Avlink Network.`;
  const companyImage = websiteData.logo || websiteData.logoUrl || websiteData.coverImage
    || 'https://www.avlinknetwork.org/images/og-image.png';

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: websiteData.fontFamily || 'Inter',
        backgroundColor: websiteData.backgroundColor || '#FFFFFF'
      }}
    >
      <SEO
        title={companyName}
        description={String(companyDesc).slice(0, 155)}
        path={`/empresa/${slug}`}
        type="website"
        image={companyImage}
        noindex={websiteData.showInSearch === false}
        jsonLd={buildCompanyJsonLd(websiteData, slug)}
      />
      {/* Hero Section */}
      {websiteData.sections?.hero && (
        <>
          <div 
            className="relative bg-cover bg-center"
            style={{ 
              backgroundColor: websiteData.primaryColor || '#3B82F6',
              backgroundImage: websiteData.hero?.backgroundImage ? `url(${websiteData.hero.backgroundImage})` : 'none'
            }}
          >
            {websiteData.hero?.backgroundImage && (
              <div 
                className="absolute inset-0"
                style={{ 
                  backgroundColor: `rgba(0, 0, 0, ${(websiteData.hero.overlayOpacity || 40) / 100})`
                }}
              />
            )}
            
            {/* Hero Content con Logo */}
            <div 
              className="relative z-10 text-center py-20 px-8" 
              style={{ color: websiteData.hero?.textColor || '#FFFFFF' }}
            >
              {/* Logo en el Hero */}
              {websiteData.about?.image && (
                <div className="mb-8">
                  <img 
                    src={websiteData.about.image}
                    alt={`Logo de ${websiteData.title || 'la empresa'}`}
                    className="w-24 h-24 md:w-32 md:h-32 object-contain mx-auto rounded-full bg-white/10 backdrop-blur-sm p-2 shadow-lg"
                    onError={(e) => {
                      console.error('❌ Error cargando logo:', websiteData.about.image);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
                {websiteData.hero?.title || websiteData.title || 'Bienvenido'}
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl mb-8 opacity-90 max-w-4xl mx-auto leading-relaxed">
                {websiteData.hero?.subtitle || websiteData.description || ''}
              </p>
              {websiteData.hero?.ctaText && (
                <button 
                  onClick={() => scrollToSection('contacto')}
                  className="px-8 py-4 rounded-lg font-medium text-lg transition-all hover:scale-105 shadow-lg cursor-pointer"
                  style={{ 
                    backgroundColor: websiteData.hero?.textColor || '#FFFFFF',
                    color: websiteData.primaryColor || '#3B82F6'
                  }}
                >
                  {websiteData.hero.ctaText}
                  <FiExternalLink className="inline ml-2" size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Navigation Bar - Sticky */}
          <nav className="sticky top-0 z-40 bg-white shadow-md border-b border-gray-200">
            <div className="max-w-6xl mx-auto px-4">
              <div className="flex items-center justify-between h-16">
                {/* Logo/Nombre */}
                <div className="flex items-center">
                  <span 
                    className="font-bold text-lg" 
                    style={{ color: websiteData.secondaryColor || '#1E293B' }}
                  >
                    {websiteData.title}
                  </span>
                </div>

                {/* Navigation Links - Desktop */}
                <div className="hidden md:flex items-center space-x-8">
                  {websiteData.sections?.about && (
                    <button 
                      onClick={() => scrollToSection('sobre-nosotros')}
                      className="text-gray-700 hover:text-gray-900 transition-colors font-medium cursor-pointer"
                    >
                      Sobre Nosotros
                    </button>
                  )}
                  {websiteData.sections?.products && websiteData.products?.items?.length > 0 && (
                    <button 
                      onClick={() => scrollToSection('productos')}
                      className="text-gray-700 hover:text-gray-900 transition-colors font-medium cursor-pointer"
                    >
                      Productos
                    </button>
                  )}
                  {websiteData.sections?.gallery && websiteData.gallery?.images?.length > 0 && (
                    <button 
                      onClick={() => scrollToSection('galeria')}
                      className="text-gray-700 hover:text-gray-900 transition-colors font-medium cursor-pointer"
                    >
                      Galería
                    </button>
                  )}
                  {websiteData.sections?.contact && (
                    <button 
                      onClick={() => scrollToSection('contacto')}
                      className="text-gray-700 hover:text-gray-900 transition-colors font-medium cursor-pointer"
                    >
                      Contacto
                    </button>
                  )}
                  
                  {/* CTA en navbar */}
                  {websiteData.contact?.phone && (
                    <a 
                      href={`tel:${websiteData.contact.phone}`}
                      className="px-4 py-2 rounded-lg font-medium transition-all hover:scale-105"
                      style={{ 
                        backgroundColor: websiteData.primaryColor || '#3B82F6',
                        color: '#FFFFFF'
                      }}
                    >
                      Llamar
                    </a>
                  )}
                </div>

                {/* Mobile menu button */}
                <div className="md:hidden">
                  <button 
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Mobile menu */}
              {mobileMenuOpen && (
                <div className="md:hidden border-t border-gray-200 py-4">
                  <div className="flex flex-col space-y-4">
                    {websiteData.sections?.about && (
                      <button 
                        onClick={() => scrollToSection('sobre-nosotros')}
                        className="text-left text-gray-700 hover:text-gray-900 font-medium"
                      >
                        Sobre Nosotros
                      </button>
                    )}
                    {websiteData.sections?.products && websiteData.products?.items?.length > 0 && (
                      <button 
                        onClick={() => scrollToSection('productos')}
                        className="text-left text-gray-700 hover:text-gray-900 font-medium"
                      >
                        Productos
                      </button>
                    )}
                    {websiteData.sections?.gallery && websiteData.gallery?.images?.length > 0 && (
                      <button 
                        onClick={() => scrollToSection('galeria')}
                        className="text-left text-gray-700 hover:text-gray-900 font-medium"
                      >
                        Galería
                      </button>
                    )}
                    {websiteData.sections?.contact && (
                      <button 
                        onClick={() => scrollToSection('contacto')}
                        className="text-left text-gray-700 hover:text-gray-900 font-medium"
                      >
                        Contacto
                      </button>
                    )}
                    {websiteData.contact?.phone && (
                      <a 
                        href={`tel:${websiteData.contact.phone}`}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium w-full"
                        style={{ 
                          backgroundColor: websiteData.primaryColor || '#3B82F6',
                          color: '#FFFFFF'
                        }}
                      >
                        Llamar
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </nav>
        </>
      )}

      {/* About Section */}
      {websiteData.sections?.about && (websiteData.about?.content || websiteData.about?.features?.length > 0) && (
        <section id="sobre-nosotros" className="py-16 px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4" 
                style={{ color: websiteData.secondaryColor || '#1E293B' }}
              >
                {websiteData.about?.title || 'Sobre Nosotros'}
              </h2>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="text-gray-600 leading-relaxed text-base md:text-lg whitespace-pre-wrap mb-8 text-center">
                {websiteData.about?.content || websiteData.description || ''}
              </div>
              
              {websiteData.about?.features?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                  {websiteData.about.features.map((feature, index) => (
                    <div key={index} className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100">
                      <div 
                        className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                        style={{ backgroundColor: `${websiteData.primaryColor || '#3B82F6'}20` }}
                      >
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: websiteData.primaryColor || '#3B82F6' }}
                        />
                      </div>
                      <p className="text-gray-700 font-medium">{feature}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* 🔧 Products Section - ARREGLADO */}
      {websiteData.sections?.products && websiteData.products?.items?.length > 0 && (
        <section id="productos" className="py-16 px-8 bg-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4" 
                style={{ color: websiteData.secondaryColor || '#1E293B' }}
              >
                {websiteData.products?.title || 'Nuestros Productos'}
              </h2>
              <p className="text-gray-600 text-lg">
                {websiteData.products?.subtitle || 'Ofrecemos una amplia gama de productos y servicios'}
              </p>
            </div>
            
            {/* 🔧 Debug info */}
            <div className="mb-4 text-sm text-gray-500 text-center">
              Se encontraron {websiteData.products.items.length} productos
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {websiteData.products.items.map((product, index) => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* 🔧 Componente de imagen arreglado */}
                  <ProductImage product={product} index={index} />
                  
                  <div className="p-6">
                    <div className="mb-2">
                      <h3 className="text-xl font-semibold mb-1" style={{ color: websiteData.secondaryColor || '#1E293B' }}>
                        {product.name}
                      </h3>
                      {(product.brand || product.model) && (
                        <p className="text-sm text-gray-500">
                          {product.brand}{product.brand && product.model && ' - '}{product.model}
                        </p>
                      )}
                      {product.category && (
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full mt-1">
                          {product.category}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {product.description}
                    </p>
                    
                    {product.features && product.features.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Características:</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          {product.features.slice(0, 3).map((feature, featureIndex) => (
                            <li key={featureIndex} className="flex items-start">
                              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                              {feature}
                            </li>
                          ))}
                          {product.features.length > 3 && (
                            <li className="text-gray-500 italic">
                              +{product.features.length - 3} características más
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {product.price && (
                      <p className="text-lg font-bold" style={{ color: websiteData.primaryColor || '#3B82F6' }}>
                        ${product.price}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Equipo (membresías técnico ↔ empresa) — solo renderiza si hay miembros activos */}
      <CompanyTeamPublicSection companyId={websiteData?.companyId} accentColor={websiteData?.primaryColor} />

      {/* Gallery Section */}
      {websiteData.sections?.gallery && websiteData.gallery?.images?.length > 0 && (
        <section id="galeria" className="py-16 px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4" 
                style={{ color: websiteData.secondaryColor || '#1E293B' }}
              >
                {websiteData.gallery?.title || 'Galería'}
              </h2>
              <p className="text-gray-600 text-lg">
                {websiteData.gallery?.subtitle || 'Conoce nuestras instalaciones y proyectos'}
              </p>
            </div>
            
            <div className="mb-4 text-sm text-gray-500 text-center">
              Se encontraron {websiteData.gallery.images.length} imágenes en la galería
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {websiteData.gallery.images.map((image, index) => (
                <GalleryImage key={index} image={image} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      {websiteData.sections?.contact && (
        <section id="contacto" className="py-16 px-8 bg-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4" 
                style={{ color: websiteData.secondaryColor || '#1E293B' }}
              >
                {websiteData.contact?.title || 'Contáctanos'}
              </h2>
              <p className="text-gray-600 text-lg">
                {websiteData.contact?.subtitle || 'Estamos aquí para ayudarte'}
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-md grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                {websiteData.contact?.address && (
                  <div className="flex items-center text-gray-700">
                    <FiMapPin className="mr-3 text-blue-500" size={20} />
                    <span>{websiteData.contact.address}</span>
                  </div>
                )}
                {websiteData.contact?.phone && (
                  <div className="flex items-center text-gray-700">
                    <FiPhone className="mr-3 text-blue-500" size={20} />
                    <a href={`tel:${websiteData.contact.phone}`} className="hover:underline">{websiteData.contact.phone}</a>
                  </div>
                )}
                {websiteData.contact?.email && (
                  <div className="flex items-center text-gray-700">
                    <FiMail className="mr-3 text-blue-500" size={20} />
                    <a href={`mailto:${websiteData.contact.email}`} className="hover:underline">{websiteData.contact.email}</a>
                  </div>
                )}
                {websiteData.contact?.website && (
                   <div className="flex items-center text-gray-700">
                    <FiGlobe className="mr-3 text-blue-500" size={20} />
                    <a href={websiteData.contact.website} target="_blank" rel="noopener noreferrer" className="hover:underline">{websiteData.contact.website}</a>
                  </div>
                )}
              </div>
              
              {websiteData.contact?.showForm && (
                <div>
                  <h3 className="text-xl font-semibold mb-4" style={{ color: websiteData.secondaryColor || '#1E293B' }}>Envíanos un mensaje</h3>
                  <form className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 text-sm font-medium mb-1">Nombre</label>
                      <input type="text" id="name" name="name" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-1">Email</label>
                      <input type="email" id="email" name="email" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-gray-700 text-sm font-medium mb-1">Mensaje</label>
                      <textarea id="message" name="message" rows="4" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"></textarea>
                    </div>
                    <button 
                      type="submit" 
                      className="px-6 py-3 rounded-lg font-medium text-lg transition-all hover:scale-105 shadow-md w-full"
                      style={{ 
                        backgroundColor: websiteData.primaryColor || '#3B82F6',
                        color: '#FFFFFF'
                      }}
                    >
                      Enviar Mensaje
                    </button>
                  </form>
                </div>
              )}
              
              {websiteData.contact?.mapEmbed && (
                <div className="md:col-span-2">
                  <h3 className="text-xl font-semibold mb-4" style={{ color: websiteData.secondaryColor || '#1E293B' }}>Nuestra Ubicación</h3>
                  <div className="aspect-w-16 aspect-h-9 w-full rounded-lg overflow-hidden shadow-md">
                    <iframe
                      src={websiteData.contact.mapEmbed}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Mapa de Ubicación"
                    ></iframe>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Cursos del instructor (si la empresa publico cursos en AVlink University) */}
      {websiteData?.companyId && (
        <section className="py-12 px-8 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <InstructorCoursesEmbed userId={websiteData.companyId} dark={false} />
          </div>
        </section>
      )}

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 rounded-full shadow-lg transition-all hover:scale-110 z-30"
          style={{ backgroundColor: websiteData.primaryColor || '#3B82F6' }}
        >
          <FiArrowUp size={20} className="text-white" />
        </button>
      )}

      {/* Footer */}
      <div className="py-8 px-8 border-t border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 text-gray-500 text-sm">
            <span>© 2024</span>
            <span 
              className="font-medium" 
              style={{ color: websiteData.secondaryColor || '#1E293B' }}
            >
              {websiteData.title || 'Tu Empresa'}
            </span>
            <span>• Powered by</span>
            <span className="font-bold text-blue-500">AVLINK</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPublicWebsite;